import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../../../config/supabaseClient';

export default function AlertasDashboard() {
  const [alertas, setAlertas] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    carregarAlertas();
  }, []);

  const carregarAlertas = async () => {
    try {
      const alertasGerados = [];

      // Verificar demandas urgentes não atendidas
      const { data: demandasUrgentes } = await supabase
        .from('view_demandas_detalhada')
        .select('*')
        .eq('prioridade', 'Urgente')
        .eq('status_id', 1); // Recebidas

      if (demandasUrgentes && demandasUrgentes.length > 0) {
        alertasGerados.push({
          id: 'urgentes',
          tipo: 'error',
          titulo: 'Demandas Urgentes Pendentes',
          descricao: `${demandasUrgentes.length} demanda(s) urgente(s) aguardando análise`,
          icone: 'fas fa-exclamation-triangle',
          acao: 'Ver Demandas',
          count: demandasUrgentes.length
        });
      }

      // Verificar demandas antigas (mais de 7 dias)
      const seteDiasAtras = new Date();
      seteDiasAtras.setDate(seteDiasAtras.getDate() - 7);

      const { data: demandasAntigas } = await supabase
        .from('view_demandas_detalhada')
        .select('*')
        .lt('created_at', seteDiasAtras.toISOString())
        .neq('status_id', 2); // Não concluídas

      if (demandasAntigas && demandasAntigas.length > 0) {
        alertasGerados.push({
          id: 'antigas',
          tipo: 'warning',
          titulo: 'Demandas em Atraso',
          descricao: `${demandasAntigas.length} demanda(s) com mais de 7 dias sem resolução`,
          icone: 'fas fa-clock',
          acao: 'Verificar',
          count: demandasAntigas.length
        });
      }

      // Verificar análises pendentes há muito tempo
      const { data: analisesPendentes } = await supabase
        .from('vw_analise_demanda')
        .select('*')
        .eq('analisedemanda_status_id', 1)
        .lt('analisedemanda_criado_em', seteDiasAtras.toISOString());

      if (analisesPendentes && analisesPendentes.length > 0) {
        alertasGerados.push({
          id: 'analises',
          tipo: 'info',
          titulo: 'Análises Pendentes',
          descricao: `${analisesPendentes.length} análise(s) pendente(s) há mais de 7 dias`,
          icone: 'fas fa-search',
          acao: 'Ver Análises',
          count: analisesPendentes.length
        });
      }

      // Verificar pico de demandas (mais de 10 hoje)
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);

      const { data: demandasHoje } = await supabase
        .from('view_demandas_detalhada')
        .select('*')
        .gte('created_at', hoje.toISOString());

      if (demandasHoje && demandasHoje.length > 10) {
        alertasGerados.push({
          id: 'pico',
          tipo: 'success',
          titulo: 'Alto Volume de Demandas',
          descricao: `${demandasHoje.length} demandas recebidas hoje - volume acima da média`,
          icone: 'fas fa-chart-line',
          acao: 'Monitorar',
          count: demandasHoje.length
        });
      }

      setAlertas(alertasGerados);
    } catch (error) {
      console.error('Erro ao carregar alertas:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAlertClass = (tipo) => {
    switch (tipo) {
      case 'error': return 'alerta-error';
      case 'warning': return 'alerta-warning';
      case 'info': return 'alerta-info';
      case 'success': return 'alerta-success';
      default: return 'alerta-info';
    }
  };

  const getAlertColor = (tipo) => {
    switch (tipo) {
      case 'error': return '#e74c3c';
      case 'warning': return '#f39c12';
      case 'info': return '#3498db';
      case 'success': return '#27ae60';
      default: return '#3498db';
    }
  };

  const handleAlertaClick = (alertaId, alertaTitulo) => {
    console.log(`Navegando para ${alertaTitulo}...`);
    
    switch (alertaId) {
      case 'urgentes':
        // Navegar para demandas com foco em urgentes
        navigate('/controle-demandas/demandas', { 
          state: { filtro: 'urgentes', titulo: 'Demandas Urgentes' } 
        });
        break;
      case 'antigas':
        // Navegar para demandas com foco em antigas
        navigate('/controle-demandas/demandas', { 
          state: { filtro: 'antigas', titulo: 'Demandas em Atraso' } 
        });
        break;
      case 'pico':
        // Navegar para demandas do dia
        navigate('/controle-demandas/demandas', { 
          state: { filtro: 'hoje', titulo: 'Demandas de Hoje' } 
        });
        break;
      case 'analises':
        // Navegar para análises pendentes
        navigate('/controle-demandas/analises', { 
          state: { filtro: 'pendentes', titulo: 'Análises Pendentes' } 
        });
        break;
      default:
        // Para outros tipos, navegar para demandas por padrão
        navigate('/controle-demandas/demandas');
        break;
    }
  };

  if (loading) {
    return (
      <div className="alertas-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (alertas.length === 0) {
    return (
      <div className="alertas-empty">
        <i className="fas fa-check-circle"></i>
        <p>Nenhum alerta no momento</p>
      </div>
    );
  }

  return (
    <div className="alertas-dashboard">
      <div className="alertas-header">
        <h3>
          <i className="fas fa-bell"></i>
          Alertas e Notificações
        </h3>
        <span className="alertas-count">{alertas.length}</span>
      </div>

      <div className="alertas-lista">
        {alertas.map(alerta => (
          <div key={alerta.id} className={`alerta-item ${getAlertClass(alerta.tipo)}`}>
            <div className="alerta-icon" style={{ color: getAlertColor(alerta.tipo) }}>
              <i className={alerta.icone}></i>
            </div>
            
            <div className="alerta-content">
              <div className="alerta-titulo">{alerta.titulo}</div>
              <div className="alerta-descricao">{alerta.descricao}</div>
            </div>
            
            <div className="alerta-badge" style={{ background: getAlertColor(alerta.tipo) }}>
              {alerta.count}
            </div>
            
            <button 
              className="alerta-acao"
              style={{ 
                color: getAlertColor(alerta.tipo),
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onClick={() => handleAlertaClick(alerta.id, alerta.titulo)}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = getAlertColor(alerta.tipo);
                e.target.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = getAlertColor(alerta.tipo);
              }}
              title={`Clique para ir para ${alerta.id === 'analises' ? 'análises' : 'demandas'}`}
            >
              {alerta.acao}
              <i className="fas fa-chevron-right" style={{ marginLeft: '6px' }}></i>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}